import { useMutation } from '@apollo/client';
import type { RemoveRiskFromEnterpriseRiskMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  RemoveRiskFromEnterpriseRiskDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useRemoveRiskFromEnterpriseRiskTRPC } from './useRemoveRiskFromEnterpriseRiskTRPC';

/**
 * Flag-gated wrapper (COI-432). Delegates to the tRPC twin when the `trpc` flag
 * is on, else the legacy Apollo Hasura-action path. Consumers import THIS, never
 * the *TRPC twin (see packages/web/CLAUDE.md → "Mutation Hook Wrapper Pattern").
 */
export const useRemoveRiskFromEnterpriseRisk = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const trpcMutation = useRemoveRiskFromEnterpriseRiskTRPC();

  const [removeRiskFromEnterpriseRiskGraphQL, { loading, error }] = useMutation(
    RemoveRiskFromEnterpriseRiskDocument,
    {
      update: (cache) => {
        evictField(cache, 'risk');
        evictField(cache, 'risk_score');
        evictField(cache, 'enterprise_risk');
        evictField(cache, 'enterprise_risk_score');
      },
      refetchQueries: [namedOperations.Query.getRiskById],
    }
  );

  const removeRiskFromEnterpriseRisk = async (
    riskId: string
  ): Promise<RemoveRiskFromEnterpriseRiskMutation | undefined> => {
    if (trpcEnabled) {
      return trpcMutation.removeRiskFromEnterpriseRisk(riskId);
    }

    const result = await removeRiskFromEnterpriseRiskGraphQL({
      variables: { RiskId: riskId },
    });
    await queryClient.invalidateQueries({
      queryKey: trpc.frontend.risk.riskById.queryKey(),
    });

    return result.data ?? undefined;
  };

  if (trpcEnabled) {
    return {
      removeRiskFromEnterpriseRisk,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return { removeRiskFromEnterpriseRisk, loading, error };
};
